import { type ReactNode } from "react";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { DenseTable, StatusBadge, type Column, type StatusLevel } from "../../components/index.ts";
import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import { useRequestsPaged } from "./data.ts";
import type { AccessRequest, RequestStatus } from "./types.ts";
import "./agentdna.css";

/**
 * Requests (AgentDNA port): the queue of agent-creation and access requests an admin reviews and
 * approves or rejects. Admin-only — a non-admin sees the restricted state (the server enforces the
 * approval actions regardless).
 *
 * UI-only port: wired to a contract-shaped stub; the approve/reject actions are rendered but inert
 * until the real write lands.
 */
export function RequestsScreen(): ReactNode {
  const { role } = useIdentity();
  const { data, loading } = useRequestsPaged(1);

  const state = resolveState(role, loading, data.items.length);

  const columns: readonly Column<AccessRequest>[] = [
    { key: "subject", header: "Subject", isRowHeader: true, cell: (r) => r.subject },
    {
      key: "kind",
      header: "Kind",
      cell: (r) => (r.kind === "agent_creation" ? "Agent creation" : "Access"),
    },
    { key: "detail", header: "Detail", cell: (r) => r.detail },
    { key: "requestedBy", header: "Requested by", cell: (r) => r.requestedBy },
    {
      key: "status",
      header: "Status",
      cell: (r) => <StatusBadge level={statusLevelFor(r.status)} label={labelFor(r.status)} size="sm" />,
    },
    {
      key: "created",
      header: "Requested",
      align: "right",
      cell: (r) => <time dateTime={isoAttr(r.created)}>{formatDateTime(r.created)}</time>,
    },
  ];

  return (
    <ScreenScaffold
      title="Requests"
      subtitle="Agent-creation and access requests awaiting review."
      state={state}
    >
      <DenseTable
        caption="Pending requests"
        columns={columns}
        rows={data.items}
        rowKey={(r) => r.id}
        emptyMessage="No requests are awaiting review."
      />
    </ScreenScaffold>
  );
}

function statusLevelFor(status: RequestStatus): StatusLevel {
  switch (status) {
    case "pending":
      return "warn";
    case "approved":
      return "healthy";
    case "rejected":
      return "error";
  }
}

function labelFor(status: RequestStatus): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function resolveState(
  role: ReturnType<typeof useIdentity>["role"],
  loading: boolean,
  count: number,
): ScreenState {
  // Requests is an admin review surface (A4/A5): a non-admin sees the restricted state; the server
  // enforces the approve/reject actions regardless.
  if (!roleAtLeast(role, "admin")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet. The server enforces access regardless."
          : "Requests review needs the admin role.",
    };
  }
  if (loading) return { kind: "loading" };
  if (count === 0) return { kind: "empty", cause: "No agent-creation or access requests are awaiting review." };
  return { kind: "ready" };
}
